import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useState, useEffect } from 'react';
import { X, Download } from 'lucide-react';
import { BlogPost } from '../../models/Post';
import DOMPurify from 'dompurify';
import DownloadSignUpModal from './DownloadSignUpModal';
import { facebookPixelEvents } from '../../utils/facebookPixel';
// import { fileService } from '../services/fileService';

interface ViewBlogPostModalProps {
    isOpen: boolean;
    onClose: () => void;
    post: BlogPost;
}

export default function ViewBlogPostModal({ isOpen, onClose, post }: ViewBlogPostModalProps) {
    const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);

    // Track when post is viewed
    useEffect(() => {
        if (isOpen && post) {
            // Track blog post view with Facebook Pixel
            facebookPixelEvents.viewContent({
                content_name: post.Title,
                content_category: 'blog_post',
                content_type: 'article',
                content_id: post.Id.toString()
            });
        }
    }, [isOpen, post]);

    const handleDownloadClick = async () => {
        // Track download click with Facebook Pixel
        facebookPixelEvents.lead({
            content_name: `${post.Title} - Resource Download`,
            content_category: 'blog_resource',
            content_type: 'download',
            content_id: post.Id.toString()
        });
        setIsDownloadModalOpen(true);
    };

    if (!post) return null;

    // Sanitize the HTML content
    const sanitizedContent = DOMPurify.sanitize(post.Content);

    // const handleDownload = () => {
    //     if (post.DownloadableFile) {
    //         const downloadUrl = fileService.getDownloadUrl(post.DownloadableFile);
    //         window.open(downloadUrl, '_blank');
    //     }
    // };

    return (
        <>
            <Transition appear show={isOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={onClose}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white p-8 text-left align-middle shadow-xl transition-all">
                                    <div className="flex justify-between items-center mb-8">
                                        <Dialog.Title as="h3" className="text-2xl font-semibold leading-6 text-gray-900">
                                            {post.Title}
                                        </Dialog.Title>
                                        <div className="flex items-center gap-4">
                                            {post.DownloadableFile && (
                                                <button
                                                    onClick={handleDownloadClick}
                                                    className="flex items-center gap-2 text-primary hover:text-primary/80"
                                                    title="Download attachment"
                                                >
                                                    <Download size={18} />
                                                    Download Resource
                                                </button>
                                            )}
                                            <button
                                                onClick={onClose}
                                                className="text-gray-400 hover:text-gray-500 transition-colors"
                                            >
                                                <X className="h-5 w-5" />
                                            </button>
                                        </div>
                                    </div>

                                    {post.ImageUrl && (
                                        <img 
                                            src={post.ImageUrl} 
                                            alt={post.Title}
                                            className="w-full h-64 object-cover mb-6 rounded"
                                        />
                                    )}

                                    <div className="prose prose-sm sm:prose lg:prose-lg max-w-none">
                                        <div 
                                            className="blog-content"
                                            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                                        />
                                    </div>

                                    {post.Tags.length > 0 && (
                                        <div className="mt-6 flex gap-2">
                                            {post.Tags.map((tag, index) => (
                                                <span 
                                                    key={index}
                                                    className="bg-gray-100 px-2 py-1 rounded-full text-xs text-gray-600"
                                                >
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    )}

                                    <div className="mt-6 flex justify-between items-center">
                                        <div className="text-sm text-gray-500">
                                            <div>Posted on: {new Date(post.CreatedAt).toLocaleDateString()}</div>
                                            <div>By {post.Author.Name}</div>
                                        </div>
                                        <button
                                            onClick={onClose}
                                            className="inline-flex justify-center rounded-md border border-transparent bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2"
                                        >
                                            Close
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>

            {post.DownloadableFile && (
                <DownloadSignUpModal
                    isOpen={isDownloadModalOpen}
                    onClose={() => setIsDownloadModalOpen(false)}
                    downloadFileName={post.DownloadableFile}
                />
            )}
        </>
    );
}